import { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import ImageUpload from '@/components/ui/ImageUpload';
import { getFullImageUrl } from '@/utils/imageUrl';
import {
  CreateSubjectData,
  SubjectRoleType,
  SubjectRiskLevel,
  SocialMediaAccount,
  SUBJECT_ROLE_FIELDS,
} from '@/services/subjects.service';

interface SubjectFormProps {
  initialValues?: Partial<CreateSubjectData>;
  onSubmit: (data: CreateSubjectData, photoFile?: File) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
  submitLabel?: string;
}

const EMPTY_FORM: CreateSubjectData = {
  firstName: '',
  lastName: '',
  roleType: 'SUSPECT',
};

const FIELD_LABELS: Record<keyof CreateSubjectData, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  roleType: 'Role',
  title: 'Title / Honorific',
  photoUrl: 'Photo',
  email: 'Email',
  phone: 'Phone',
  addressLine1: 'Address Line 1',
  addressLine2: 'Address Line 2',
  city: 'City',
  state: 'State / Region',
  postalCode: 'Postal Code',
  country: 'Country',
  riskLevel: 'Risk Level',
  distanceFromOfficeMiles: 'Distance from Office (miles)',
  vehicleMake: 'Vehicle Make',
  vehicleModel: 'Vehicle Model',
  vehicleColor: 'Vehicle Color',
  vehiclePlate: 'Vehicle Plate',
  socialMedia: 'Social Media',
  criminalHistory: 'Criminal History',
  supervisor: 'Supervisor',
  agency: 'Agency',
  jurisdiction: 'Jurisdiction',
  badgeNumber: 'Badge Number',
  notes: 'Notes',
};

const ROLE_OPTIONS: Array<{ value: SubjectRoleType; label: string }> = [
  { value: 'SUSPECT', label: 'Suspect' },
  { value: 'VICTIM', label: 'Victim' },
  { value: 'WITNESS', label: 'Witness' },
  { value: 'LAW_ENFORCEMENT', label: 'Law Enforcement' },
  { value: 'REPORTING_PARTY', label: 'Reporting Party' },
  { value: 'OTHER', label: 'Other' },
];

const RISK_OPTIONS: Array<{ value: SubjectRiskLevel; label: string }> = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

const inputClass =
  'w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white';

export const SubjectForm: React.FC<SubjectFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  submitting = false,
  submitLabel = 'Save Subject',
}) => {
  const [formData, setFormData] = useState<CreateSubjectData>(() => ({
    ...EMPTY_FORM,
    ...initialValues,
  }));
  const [socialMedia, setSocialMedia] = useState<SocialMediaAccount[]>(
    () => initialValues?.socialMedia ?? [],
  );
  const [photoFile, setPhotoFile] = useState<File | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const visibleFields = useMemo(() => {
    const roleFields = SUBJECT_ROLE_FIELDS[formData.roleType];
    return new Set(roleFields);
  }, [formData.roleType]);

  const handleField = <K extends keyof CreateSubjectData>(key: K, value: CreateSubjectData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First name and last name are required.');
      return;
    }

    try {
      const payload: CreateSubjectData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        socialMedia: visibleFields.has('socialMedia') && socialMedia.length > 0 ? socialMedia : undefined,
      };

      Object.keys(payload).forEach((key) => {
        const field = key as keyof CreateSubjectData;
        if (!visibleFields.has(field) && field !== 'firstName' && field !== 'lastName' && field !== 'roleType') {
          delete payload[field];
        }
        if (payload[field] === '') {
          delete payload[field];
        }
      });

      // photoUrl is set by the dedicated upload endpoint, not the create/update body.
      delete payload.photoUrl;

      await onSubmit(payload, photoFile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save subject');
    }
  };

  const renderTextInput = (
    field: keyof CreateSubjectData,
    type: 'text' | 'email' | 'tel' | 'number' | 'url' = 'text',
  ) => {
    if (!visibleFields.has(field)) { return null; }
    const rawValue = formData[field];
    const value = typeof rawValue === 'string' || typeof rawValue === 'number' ? String(rawValue) : '';

    return (
      <div key={field}>
        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
          {FIELD_LABELS[field]}
        </label>
        <input
          type={type}
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            if (type === 'number') {
              handleField(field, (raw === '' ? undefined : Number(raw)) as CreateSubjectData[typeof field]);
            } else {
              handleField(field, raw as CreateSubjectData[typeof field]);
            }
          }}
          className={inputClass}
        />
      </div>
    );
  };

  const renderTextarea = (field: keyof CreateSubjectData, rows = 3) => {
    if (!visibleFields.has(field)) { return null; }
    const rawValue = formData[field];
    const value = typeof rawValue === 'string' ? rawValue : '';
    return (
      <div key={field} className="md:col-span-2">
        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
          {FIELD_LABELS[field]}
        </label>
        <textarea
          rows={rows}
          value={value}
          onChange={(e) => handleField(field, e.target.value as CreateSubjectData[typeof field])}
          className={inputClass}
        />
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Role <span className="text-danger-500">*</span>
            </label>
            <select
              value={formData.roleType}
              onChange={(e) => handleField('roleType', e.target.value as SubjectRoleType)}
              className={inputClass}
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div />

          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              First Name <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleField('firstName', e.target.value)}
              className={inputClass}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Last Name <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleField('lastName', e.target.value)}
              className={inputClass}
              required
            />
          </div>

          {renderTextInput('title')}
          {renderTextInput('email', 'email')}
          {renderTextInput('phone', 'tel')}
          {visibleFields.has('photoUrl') && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                {FIELD_LABELS.photoUrl}
              </label>
              <ImageUpload
                onImageSelect={(file) => setPhotoFile(file)}
                currentImageUrl={getFullImageUrl(initialValues?.photoUrl) ?? undefined}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {(visibleFields.has('addressLine1') || visibleFields.has('city')) && (
        <Card>
          <CardHeader>
            <CardTitle>Address</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderTextInput('addressLine1')}
            {renderTextInput('addressLine2')}
            {renderTextInput('city')}
            {renderTextInput('state')}
            {renderTextInput('postalCode')}
            {renderTextInput('country')}
            {visibleFields.has('distanceFromOfficeMiles') && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  {FIELD_LABELS.distanceFromOfficeMiles}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.distanceFromOfficeMiles ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleField('distanceFromOfficeMiles', v === '' ? undefined : Number(v));
                  }}
                  className={inputClass}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {visibleFields.has('riskLevel') && (
        <Card>
          <CardHeader>
            <CardTitle>Risk Assessment</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Risk Level
              </label>
              <select
                value={formData.riskLevel ?? ''}
                onChange={(e) =>
                  handleField('riskLevel', (e.target.value || undefined) as SubjectRiskLevel | undefined)
                }
                className={inputClass}
              >
                <option value="">— Unknown —</option>
                {RISK_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {renderTextarea('criminalHistory', 4)}
          </CardContent>
        </Card>
      )}

      {visibleFields.has('vehicleMake') && (
        <Card>
          <CardHeader>
            <CardTitle>Vehicle</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderTextInput('vehicleMake')}
            {renderTextInput('vehicleModel')}
            {renderTextInput('vehicleColor')}
            {renderTextInput('vehiclePlate')}
          </CardContent>
        </Card>
      )}

      {visibleFields.has('socialMedia') && (
        <Card>
          <CardHeader>
            <CardTitle>Social Media</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {socialMedia.map((acc, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-2">
                <input
                  type="text"
                  placeholder="Platform"
                  value={acc.platform}
                  onChange={(e) => {
                    const next = [...socialMedia];
                    next[idx] = { ...next[idx], platform: e.target.value };
                    setSocialMedia(next);
                  }}
                  className={inputClass}
                />
                <input
                  type="text"
                  placeholder="Handle"
                  value={acc.handle ?? ''}
                  onChange={(e) => {
                    const next = [...socialMedia];
                    next[idx] = { ...next[idx], handle: e.target.value };
                    setSocialMedia(next);
                  }}
                  className={inputClass}
                />
                <input
                  type="url"
                  placeholder="URL"
                  value={acc.url ?? ''}
                  onChange={(e) => {
                    const next = [...socialMedia];
                    next[idx] = { ...next[idx], url: e.target.value };
                    setSocialMedia(next);
                  }}
                  className={inputClass}
                />
                <button
                  type="button"
                  onClick={() => setSocialMedia(socialMedia.filter((_, i) => i !== idx))}
                  className="px-3 py-2 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg"
                  aria-label="Remove social account"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => setSocialMedia([...socialMedia, { platform: '' }])}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Account
            </Button>
          </CardContent>
        </Card>
      )}

      {visibleFields.has('agency') && (
        <Card>
          <CardHeader>
            <CardTitle>Law Enforcement</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderTextInput('agency')}
            {renderTextInput('jurisdiction')}
            {renderTextInput('badgeNumber')}
          </CardContent>
        </Card>
      )}

      {visibleFields.has('supervisor') && (
        <Card>
          <CardHeader>
            <CardTitle>Additional</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderTextInput('supervisor')}
          </CardContent>
        </Card>
      )}

      {visibleFields.has('notes') && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              rows={5}
              value={formData.notes ?? ''}
              onChange={(e) => handleField('notes', e.target.value)}
              className={inputClass}
              placeholder="General notes, observations, or additional context..."
            />
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default SubjectForm;
